import { TODO } from "@revelate/types";
import { supabaseClient } from "./client";

// TODO: Map the output TS type for each
export type TableType =
  | "companies"
  | "plans"
  | "quotas"
  | "currencies"
  | "exchange_rates"
  | "deals"
  | "tokens"
  | "users"
  | "teams";

// TODO: Some possibly undocumented types are coming out of the current queries
export const getTableColumns = (table: TableType) => {
  switch (table) {
    // SignedInCompany
    case "companies":
      return `
        *,
        teams(*, users(*)),
        plans(*),
        quotas(*),
        users!public_profiles_company_id_fkey(*, currency:currencies!users_currency_id_fkey(*)),
        default_currency:currencies!companies_default_currency_id_fkey(*, exchange_rates!exchange_rates_to_currency_id_fkey(*))
      `;

    // NestedPlan
    case "plans":
      return `
        *,
        teams(*, users(*)),
        users(*),
        accelerators(*, conditions(*))
      `;

    // NestedQuota
    case "quotas":
      return `
        *,
        currency:currencies(*),
        users(*),
        goals(*, users(*))
      `;

    // NestedCurrency
    case "currencies":
      return `
        *,
        exchange_rates!exchange_rates_from_currency_id_fkey(*)
      `;

    // Pick<NestedToken, ... >
    // TODO: Looks like an ad-hoc type. Investigate.
    case "tokens":
      return "id, provider, created_at, updated_at, is_expired, allow_update";

    // NestedExchangeRate
    case "exchange_rates":
      return "*";

    // NestedDeal
    case "deals":
      return `
        *,
        users:user_deals(user_id, role, data:users(*))
      `;

    // User & { currency: Currency }
    // TODO: Looks like an ad-hoc type. Investigate.
    case "users":
      return `
        *,
        currency:currencies!users_currency_id_fkey(*)
      `;

    // NestedTeam
    case "teams":
      return `
        *,
        users(*)
      `;

    // Full table
    default:
      return "*";
  }
};

export const getAll = async (
  table: TableType,
  companyId: string | null,
  activeOnly = true,
  userRole?: string
): Promise<TODO[]> => {
  let startTime: number | undefined = undefined;

  const query = supabaseClient
    .from(table)
    .select(getTableColumns(table))
    .eq("company_id", Number(companyId));

  if (table === "users") {
    query.neq("role", "super_admin");
    if (activeOnly) {
      query.eq("is_active", true);
    }
  }

  if (table === "quotas" && userRole !== "super_admin") {
    query.eq("is_hidden", false);
  }

  if (table === "plans") {
    query.order("name", { ascending: true });
  }

  if (table === "teams") {
    query.order("name", { ascending: true });
  }

  if (table === "deals") {
    // FIXME: This arbitrary limit means we are fetching an incomplete list
    // and then calculating off that incorrect list
    // Investigate possible request timeout issues when fetched without limit
    // Also consider passing the daterange here when deals are fetched
    // so we can selectively fetch the right deals and get a better result even with a limit
    query.limit(1_000);

    startTime = performance.now();
  }

  const { data, error } = await query.returns<TODO[]>();

  if (table === "deals" && startTime !== undefined) {
    const endTime = performance.now();
    console.warn(
      `Fetching ${data?.length ?? 0} deals took ${(endTime - startTime).toFixed(0)} ms`
    );
  }

  if (error) {
    throw error;
  }

  return data;
};

export const get = async (
  table: TableType,
  id: string | null,
  activeOnly = true
): Promise<TODO | null> => {
  if (!id) {
    return null;
  }

  const query = supabaseClient
    .from(table)
    .select(getTableColumns(table))
    .eq("id", Number(id));

  if (table === "companies") {
    query.neq("users.role", "super_admin");
    if (activeOnly) {
      query.eq("users.is_active", true);
    }
  }

  const { data, error } = await query.returns<TODO[]>().maybeSingle();
  if (error) {
    throw error;
  }
  return data;
};
